import { useCallback, useEffect, useRef, useState } from 'react';
import { certificateApi } from '../../services/certificateApi';

export const ACTIVE_BATCH_STORAGE_KEY = 'certificate.activeBatch';

const safeMessage = (error) =>
  typeof error?.message === 'string' && error.message.trim()
    ? error.message.trim()
    : 'No fue posible completar el lote de constancias';

const readStoredBatch = () => {
  try {
    const value = localStorage.getItem(ACTIVE_BATCH_STORAGE_KEY);
    if (!value) return null;
    const record = JSON.parse(value);
    if (
      !record ||
      typeof record !== 'object' ||
      typeof record.request_id !== 'string' ||
      !record.request_id.trim()
    ) {
      localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
      return null;
    }
    return record;
  } catch (_error) {
    localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
    return null;
  }
};

const storedBatchId = (record) => {
  const id = Number(record?.id_lote);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const persistBatch = (record) => {
  localStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, JSON.stringify(record));
};

const pendingCount = (batch) => {
  const count = Number(batch?.pendientes);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const defaultRequestId = () => {
  if (typeof window.crypto?.randomUUID !== 'function') {
    throw new Error('El navegador no puede crear un identificador seguro');
  }
  return window.crypto.randomUUID();
};

export const useCertificateBatch = ({
  api = certificateApi,
  createRequestId = defaultRequestId,
} = {}) => {
  const [batch, setBatch] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  const operationRef = useRef(0);
  const runningRef = useRef(false);

  const isActive = useCallback(
    (operationId) =>
      mountedRef.current && operationId === operationRef.current,
    [],
  );

  const publishBatch = useCallback(
    (operationId, nextBatch) => {
      if (isActive(operationId)) {
        setBatch(nextBatch);
      }
    },
    [isActive],
  );

  const processUntilSettled = useCallback(
    async (
      idLote,
      operationId,
      initialBatch = null,
      probeProcessing = false,
    ) => {
      let next = initialBatch || (await api.getBatch(idLote));
      publishBatch(operationId, next);

      if (
        probeProcessing &&
        isActive(operationId) &&
        pendingCount(next) === 0 &&
        next?.estado === 'procesando'
      ) {
        next = await api.processChunk(idLote);
        publishBatch(operationId, next);
      }

      while (isActive(operationId) && pendingCount(next) > 0) {
        next = await api.processChunk(idLote);
        publishBatch(operationId, next);
      }
      return next;
    },
    [api, isActive, publishBatch],
  );

  const beginOperation = useCallback(() => {
    const operationId = operationRef.current + 1;
    operationRef.current = operationId;
    runningRef.current = true;
    setRunning(true);
    setError('');
    return operationId;
  }, []);

  const finishOperation = useCallback(
    (operationId) => {
      if (isActive(operationId)) {
        runningRef.current = false;
        setRunning(false);
      }
    },
    [isActive],
  );

  const start = useCallback(
    async (payload) => {
      if (runningRef.current) return null;

      const stored = readStoredBatch();
      if (storedBatchId(stored)) {
        setError('Ya existe un lote activo. Reanúdalo o crea otro lote.');
        return null;
      }

      const operationId = beginOperation();
      try {
        const requestId = stored?.request_id || createRequestId();
        persistBatch({ request_id: requestId, id_lote: null });
        const created = await api.createBatch({
          ...payload,
          request_id: requestId,
        });
        const idLote = Number(created?.id_lote);
        if (!Number.isInteger(idLote) || idLote <= 0) {
          throw new Error('El servidor no devolvió un lote válido');
        }
        persistBatch({ request_id: requestId, id_lote: idLote });
        return await processUntilSettled(idLote, operationId, created);
      } catch (requestError) {
        if (isActive(operationId)) {
          setError(safeMessage(requestError));
        }
        return null;
      } finally {
        finishOperation(operationId);
      }
    },
    [
      api,
      beginOperation,
      createRequestId,
      finishOperation,
      isActive,
      processUntilSettled,
    ],
  );

  const resume = useCallback(async () => {
    if (runningRef.current) return null;
    const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
    if (!Number.isInteger(idLote) || idLote <= 0) {
      setError('No hay un lote pendiente para reanudar');
      return null;
    }

    const operationId = beginOperation();
    try {
      return await processUntilSettled(idLote, operationId, null, true);
    } catch (requestError) {
      if (isActive(operationId)) {
        setError(safeMessage(requestError));
      }
      return null;
    } finally {
      finishOperation(operationId);
    }
  }, [
    batch,
    beginOperation,
    finishOperation,
    isActive,
    processUntilSettled,
  ]);

  const retryFailed = useCallback(async () => {
    if (runningRef.current) return null;
    const idLote = storedBatchId(readStoredBatch()) || Number(batch?.id_lote);
    if (!Number.isInteger(idLote) || idLote <= 0) {
      setError('No hay un lote con errores para reintentar');
      return null;
    }

    const operationId = beginOperation();
    try {
      const reopened = await api.retryFailed(idLote);
      publishBatch(operationId, reopened);
      return await processUntilSettled(idLote, operationId, reopened);
    } catch (requestError) {
      if (isActive(operationId)) {
        setError(safeMessage(requestError));
      }
      return null;
    } finally {
      finishOperation(operationId);
    }
  }, [
    api,
    batch,
    beginOperation,
    finishOperation,
    isActive,
    processUntilSettled,
    publishBatch,
  ]);

  const clear = useCallback(() => {
    operationRef.current += 1;
    runningRef.current = false;
    localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
    setBatch(null);
    setError('');
    setRunning(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const stored = readStoredBatch();
    const idLote = storedBatchId(stored);
    if (!idLote) {
      return () => {
        mountedRef.current = false;
        operationRef.current += 1;
      };
    }

    const operationId = operationRef.current + 1;
    operationRef.current = operationId;
    api
      .getBatch(idLote)
      .then((restored) => publishBatch(operationId, restored))
      .catch((requestError) => {
        if (isActive(operationId)) {
          setError(safeMessage(requestError));
        }
      });

    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
      runningRef.current = false;
    };
  }, [api, isActive, publishBatch]);

  return { batch, running, error, start, resume, retryFailed, clear };
};
